import React, { useState } from 'react';
import { Input, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { getTemplateText, useRejectReasonTemplates } from '../hooks/useRejectReasonTemplates';

interface RejectReasonModalProps {
  open: boolean;
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  open,
  loading,
  onConfirm,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState(false);
  const [templateCode, setTemplateCode] = useState<string | undefined>(undefined);
  const { templates, loading: templatesLoading } = useRejectReasonTemplates(!open);

  const templateOptions = templates.map((template) => ({
    value: template.code,
    label: getTemplateText(template, i18n.language),
  }));

  const handleConfirm = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(true);
      return;
    }
    onConfirm(trimmed);
  };

  const handleCancel = () => {
    setReason('');
    setError(false);
    setTemplateCode(undefined);
    onCancel();
  };

  // 选中模版后整段带入输入框（覆盖已输入内容），仍可继续编辑
  const handleTemplateChange = (code?: string) => {
    setTemplateCode(code);
    const template = templates.find((item) => item.code === code);
    if (!template) return;
    const text = getTemplateText(template, i18n.language);
    setReason(text);
    if (text) setError(false);
  };

  // 每次打开时重置内容
  React.useEffect(() => {
    if (open) {
      setReason('');
      setError(false);
      setTemplateCode(undefined);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      title={t('partnerControl.appealReview.action.rejectModalTitle')}
      okText={t('common.confirm', 'OK')}
      cancelText={t('common.cancel', 'Cancel')}
      okButtonProps={{ danger: true, loading }}
      onOk={handleConfirm}
      onCancel={handleCancel}
      destroyOnClose
    >
      <div className="py-2">
        <div className="mb-1 text-sm font-medium">
          {t('partnerControl.appealReview.action.rejectTemplateLabel')}
        </div>
        <Select
          className="w-full mb-3"
          allowClear
          showSearch
          optionFilterProp="label"
          loading={templatesLoading}
          options={templateOptions}
          value={templateCode}
          onChange={handleTemplateChange}
          placeholder={t('partnerControl.appealReview.action.rejectTemplatePlaceholder') as string}
        />
        <div className="mb-1 text-sm font-medium">
          {t('partnerControl.appealReview.action.rejectReasonLabel')}
          <span className="text-red-500 ml-0.5">*</span>
        </div>
        <Input.TextArea
          rows={4}
          maxLength={128}
          showCount
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value.trim()) setError(false);
          }}
          placeholder={t('partnerControl.appealReview.action.rejectReasonPlaceholder') as string}
          status={error ? 'error' : undefined}
        />
        {error && (
          <div className="mt-1 text-xs text-red-500">
            {t('partnerControl.appealReview.action.rejectReasonRequired')}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RejectReasonModal;
